import { computed, reactive, readonly } from 'vue'
import {
  createProjectDraftSettings,
  getProjectDraftSettings,
  updateProjectDraftSettings,
} from '@/services/projectSettings'
import { ParticipationSetting, SETTINGS_METADATA, type Settings } from '@/constants/Settings'

/** The draft-setting keys that back the preview-comment permission panel. */
const PREVIEW_COMMENT_SETTING_KEYS: string[] = [
  ParticipationSetting.PreviewCommentsEveryone,
  ParticipationSetting.PreviewCommentsRestricted,
  ParticipationSetting.PreviewCommentsEmails,
]

/**
 * Shared reactive state for the preview-comment permission settings shown in the
 * Velt panel's "Settings & Permissions" tab.
 *
 * Storage format mirrors the project-settings pipeline (see VELT_PREVIEW_COMMENTS_PLAN.md):
 *  - `everyone`   → `preview_comments_everyone`   (boolean)
 *  - `restricted` → `preview_comments_restricted` (boolean)
 *  - `emails`     → `preview_comments_emails`      (CSV string of allowed emails)
 *
 * Precedence rule: `everyone` wins over `restricted`.
 */
export interface PreviewCommentsSettings {
  everyone: boolean
  restricted: boolean
  /** Allowed emails, held as a list in-memory; serialised to CSV for persistence. */
  emails: string[]
}

const state = reactive<PreviewCommentsSettings>({
  everyone: false,
  restricted: false,
  emails: [],
})

/** True when the restricted allow-list controls should be active/editable. */
export const restrictedActive = computed(() => !state.everyone && state.restricted)

export const previewCommentsState = readonly(state)

export function setEveryone(value: boolean): void {
  state.everyone = value
  // "everyone" wins — restricting is meaningless while everyone can comment.
  if (value) {
    state.restricted = false
  }
}

export function setRestricted(value: boolean): void {
  state.restricted = value
  // Selecting "only specific people" turns "everyone" off — they're mutually
  // exclusive, mirroring setEveryone().
  if (value) {
    state.everyone = false
  }
}

export function setEmails(emails: string[]): void {
  state.emails = normaliseEmails(emails)
}

/** Normalise, trim and de-duplicate a list of emails. */
export function normaliseEmails(emails: string[]): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const raw of emails) {
    const email = raw.trim()
    if (!email) continue
    const key = email.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    result.push(email)
  }
  return result
}

/** CSV form for persistence, e.g. "a@x.com,b@y.com". */
export function emailsToCsv(): string {
  return state.emails.join(',')
}

/** Hydrate the list from a CSV string (e.g. from a persisted setting). */
export function setEmailsFromCsv(csv: string): void {
  setEmails((csv || '').split(','))
}

/**
 * Set the panel state to the exact persisted values, without applying the
 * "everyone wins" side effect that {@link setEveryone} uses for user edits.
 * Used when hydrating from the authoritative draft settings.
 */
export function hydrate(everyone: boolean, restricted: boolean, emailsCsv: string): void {
  state.everyone = everyone
  state.restricted = restricted
  state.emails = normaliseEmails((emailsCsv || '').split(','))
}

/**
 * Resolve a persisted boolean draft value, falling back to the project's
 * configured default (see {@link SETTINGS_METADATA}) when the key is absent.
 * A freshly created project draft won't have the preview-comment keys yet, so
 * treating "missing" as `false` would wrongly hide comments even though the
 * project default for e.g. {@link ParticipationSetting.PreviewCommentsEveryone}
 * is now `true`.
 */
function resolveBool(byName: Map<string, string>, key: ParticipationSetting): boolean {
  const raw = byName.get(key)
  if (raw === undefined) return SETTINGS_METADATA[key].defaultValue as boolean
  return raw === 'true'
}

/** As {@link resolveBool}, for string-valued settings (e.g. the emails CSV). */
function resolveString(byName: Map<string, string>, key: ParticipationSetting): string {
  const raw = byName.get(key)
  if (raw === undefined) return SETTINGS_METADATA[key].defaultValue as string
  return raw
}

/**
 * Hydrate the panel state from an already-loaded {@link Settings} instance —
 * used during embedded init (App.vue) where the draft settings are fetched once
 * for the whole app. `Settings` applies the metadata defaults internally, so
 * missing keys resolve to their project defaults rather than `false`.
 */
export function hydrateFromSettings(settings: Pick<Settings, 'getSetting'>): void {
  hydrate(
    settings.getSetting(ParticipationSetting.PreviewCommentsEveryone),
    settings.getSetting(ParticipationSetting.PreviewCommentsRestricted),
    settings.getSetting(ParticipationSetting.PreviewCommentsEmails),
  )
}

/**
 * Refresh the panel from core's draft project settings — the same source the
 * persist path writes to — so the "Settings & Permissions" tab always reflects
 * the latest saved values rather than the survey session's start-of-run
 * snapshot. Only meaningful embedded (main-app bridge available).
 */
export async function refreshFromDraft(): Promise<void> {
  try {
    const response = await getProjectDraftSettings(PREVIEW_COMMENT_SETTING_KEYS)
    const byName = new Map<string, string>(
      (response?.projectDraftSettingsList ?? []).map((s) => [s.name, s.value]),
    )
    hydrate(
      resolveBool(byName, ParticipationSetting.PreviewCommentsEveryone),
      resolveBool(byName, ParticipationSetting.PreviewCommentsRestricted),
      resolveString(byName, ParticipationSetting.PreviewCommentsEmails),
    )
  } catch (error) {
    // Callers fire-and-forget (`void refreshFromDraft()`), so swallow the
    // failure here to avoid an unhandled promise rejection when the bridge is
    // temporarily unavailable. The panel simply keeps its current state.
    console.warn('Failed to refresh preview-comment settings from draft:', error)
  }
}

/**
 * Persist the current settings back to core's draft project settings via the
 * main-app bridge. Only works while the preview runs embedded in the shell
 * (the same channel `App.vue` uses to read settings on init).
 */
export async function persistCommentsSettings(): Promise<void> {
  const desired: { [key: string]: string } = {
    [ParticipationSetting.PreviewCommentsEveryone]: String(state.everyone),
    [ParticipationSetting.PreviewCommentsRestricted]: String(state.restricted),
    [ParticipationSetting.PreviewCommentsEmails]: emailsToCsv(),
  }

  // `UpdateProjectDraftSettings` only mutates keys that already exist on the
  // draft; calling it for a key core has never seen fails with
  // "setting '<name>' does not exist in project draft settings". A freshly
  // created project draft (as in non-local environments) won't have these
  // preview-comment keys yet, so we must CREATE missing keys and UPDATE the
  // rest — an upsert.
  const existing = new Set<string>(
    (await getProjectDraftSettings(Object.keys(desired)))?.projectDraftSettingsList?.map(
      (s) => s.name,
    ) ?? [],
  )

  const toCreate: { [key: string]: string } = {}
  const toUpdate: { [key: string]: string } = {}
  for (const [name, value] of Object.entries(desired)) {
    if (existing.has(name)) {
      toUpdate[name] = value
    } else {
      toCreate[name] = value
    }
  }

  if (Object.keys(toCreate).length > 0) {
    await createProjectDraftSettings(toCreate)
  }
  if (Object.keys(toUpdate).length > 0) {
    await updateProjectDraftSettings(toUpdate)
  }
}

export function useCommentsSettings() {
  return {
    settings: state,
    restrictedActive,
    setEveryone,
    setRestricted,
    setEmails,
    emailsToCsv,
    setEmailsFromCsv,
    hydrate,
    hydrateFromSettings,
    refreshFromDraft,
    persistCommentsSettings,
  }
}



